import React from 'react'
import { useTranslation } from 'react-i18next'
import { InputLabel } from '@node-space/storybook-components/dist/FormElements'
import Input from '@node-space/storybook-components/dist/Input'
import Select from '@node-space/storybook-components/dist/Select'
import { Box } from '@node-space/ui-components'
import { useCountryStatesQuery } from 'hooks/queries/useCountriesQuery'
import { AddressFields } from './types'
import { getIsStateRequired } from './utils'

export interface AddressFormFieldsProps {
  label?: string
  addressFields: AddressFields
  isLoadingCountries: boolean
  testId?: string
}

export const AddressFormFields = ({
  label,
  addressFields,
  isLoadingCountries,
  testId,
}: AddressFormFieldsProps) => {
  const { t } = useTranslation()
  const testIdPrefix = testId ? `${testId}-` : ''

  const countryCode = addressFields?.countryCode?.value
  const isStateRequired = getIsStateRequired(countryCode) && !!addressFields?.stateCode

  const {
    data: stateOptions = [],
    isFetching: isFetchingStateOptions,
    isError: isErrorFetchingStateOptions,
  } = useCountryStatesQuery(isStateRequired && countryCode)

  return (
    <Box>
      <InputLabel forId={addressFields?.address1?.name}>
        {label ?? t('formFields.residentialAddress')}
      </InputLabel>
      <Box flexCol gapY={8}>
        <Input {...addressFields?.address1} testid={`${testIdPrefix}address1-input`} />
        <Input {...addressFields?.address2} testid={`${testIdPrefix}address2-input`} />
        <Box flex gap={8}>
          <Box flexSize="fill" width="full">
            <Input {...addressFields?.city} testid={`${testIdPrefix}city-input`} />
          </Box>
          <Box flexSize="fill" width="full">
            <Input {...addressFields?.postalCode} testid={`${testIdPrefix}postal-code-input`} />
          </Box>
        </Box>
        <Select
          {...addressFields?.countryCode}
          loading={isLoadingCountries}
          isSearchable
          testid={`${testIdPrefix}country-select`}
        />
        {isStateRequired && (
          <Select
            {...addressFields?.stateCode}
            options={stateOptions}
            loading={isFetchingStateOptions}
            isSearchable
            testid={`${testIdPrefix}country-state-select`}
            error={isErrorFetchingStateOptions || addressFields?.stateCode?.error}
            errorText={
              isErrorFetchingStateOptions
                ? t('requestErrors.fieldOptions')
                : addressFields?.stateCode?.errorText
            }
          />
        )}
      </Box>
    </Box>
  )
}
